if (!customElements.get('quick-add-modal')) {
  const QuickAddModalBase =
    window.ModalDialog ||
    customElements.get('modal-dialog') ||
    HTMLElement;

  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends QuickAddModalBase {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.dialog = this.querySelector('.quick-add-modal__content');
        this.openedBy = null;
        this._boundEscHandler = this._handleEsc.bind(this);
      }

      connectedCallback() {
        if (typeof super.connectedCallback === 'function') {
          super.connectedCallback();
        }

        this.modalContent =
          this.modalContent || this.querySelector('[id^="QuickAddInfo-"]');
        this.dialog =
          this.dialog || this.querySelector('.quick-add-modal__content');

        this.querySelectorAll('.quick-add-modal__toggle, [id^="ModalClose-"]').forEach((button) => {
          if (button.dataset.quickAddBound === 'true') return;

          button.addEventListener('click', () => this.hide());
          button.dataset.quickAddBound = 'true';
        });
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this._boundEscHandler);

        if (typeof super.disconnectedCallback === 'function') {
          super.disconnectedCallback();
        }
      }

      _handleEsc(event) {
        if (event.key === 'Escape') {
          this.hide(true);
        }
      }

      _fallbackShow() {
        this.removeAttribute('hidden');
        this.setAttribute('open', '');
        this.classList.add('is-open');

        if (this.dialog) {
          this.dialog.setAttribute('open', '');
          this.dialog.setAttribute('aria-hidden', 'false');
        }

        document.documentElement.classList.add('overflow-hidden');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this._boundEscHandler);

        const focusTarget =
          this.querySelector('.quick-add-modal__toggle, [id^="ModalClose-"]') ||
          this.dialog;

        if (focusTarget && typeof focusTarget.focus === 'function') {
          focusTarget.focus();
        }
      }

      _fallbackHide() {
        this.removeAttribute('open');
        this.classList.remove('is-open');

        if (this.dialog) {
          this.dialog.removeAttribute('open');
          this.dialog.setAttribute('aria-hidden', 'true');
        }

        document.documentElement.classList.remove('overflow-hidden');
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this._boundEscHandler);
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        if (
          cartNotification &&
          this.openedBy &&
          typeof cartNotification.setActiveElement === 'function'
        ) {
          cartNotification.setActiveElement(this.openedBy);
        }

        if (this.modalContent) {
          this.modalContent.innerHTML = '';
        }

        if (typeof super.hide === 'function') {
          super.hide();
        } else {
          this._fallbackHide();
        }

        if (!preventFocus && this.openedBy && typeof this.openedBy.focus === 'function') {
          this.openedBy.focus();
        }

        if (preventFocus) {
          this.openedBy = null;
        }
      }

      show(opener) {
        if (!opener) return;

        this.openedBy = opener;

        const spinner = opener.querySelector('.loading-overlay__spinner');
        const productUrl = opener.getAttribute('data-product-url');

        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');

        if (spinner) {
          spinner.classList.remove('hidden');
        }

        fetch(productUrl)
          .then((response) => {
            if (!response.ok) {
              throw new Error('Quick add request failed');
            }
            return response.text();
          })
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

            this.productElement = responseHTML.querySelector(
              'section[id^="MainProduct-"]'
            );

            if (!this.productElement || !this.modalContent) {
              throw new Error('Quick add product section not found');
            }

            this.preventDuplicatedIDs();
            this.removeDOMElements();

            const mediaGallery = this.productElement.querySelector('media-gallery');
            if (mediaGallery) {
              mediaGallery.dataset.disablePrepend = 'false';
            }

            this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) {
              window.ProductModel.loadShopifyXR();
            }

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();

            if (typeof super.show === 'function') {
              super.show(opener);
            } else {
              this._fallbackShow();
            }
          })
          .catch(() => {
            if (productUrl) {
              window.location.href = productUrl;
            }
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');

            if (spinner) {
              spinner.classList.add('hidden');
            }
          });
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');

          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });

          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );

          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        if (!this.modalContent) return;

        const variantPicker = this.modalContent.querySelector(
          'variant-radios,variant-selects'
        );
        if (!variantPicker) return;

        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        if (!this.productElement) return;

        const pickupAvailability =
          this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const stickyAtc = this.productElement.querySelector('sticky-atc');
        if (stickyAtc) stickyAtc.remove();

        const modalDialog = this.productElement.querySelectorAll('modal-dialog');
        if (modalDialog.length) {
          modalDialog.forEach((modal) => modal.remove());
        }
      }

      preventDuplicatedIDs() {
        if (!this.productElement) return;

        const sectionId = this.productElement.dataset.section;
        if (!sectionId) return;

        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(
          sectionId,
          `quickadd-${sectionId}`
        );

        this.productElement
          .querySelectorAll('variant-selects, variant-radios, product-info')
          .forEach((element) => {
            element.dataset.originalSection = sectionId;
          });
      }

      removeGalleryListSemantic() {
        if (!this.modalContent) return;

        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes() {
        if (!this.modalContent) return;

        const product = this.modalContent.querySelector('.product');
        if (!product) return;

        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}